import { usePromotions } from "../context/PromotionContext";
import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { GoSearch } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  getProductAvailableQuantity,
  sortProductsByAvailability,
} from "../utils/productAvailability";
import { buildProductDetailPath } from "../utils/productLinks";
import { getProductImage as pickProductImage } from "../utils/productImages";
import { getProductDisplayName } from "../utils/productDisplayName";
import { isProductSearchSuggestionEligible } from "../utils/productSearchEligibility";
import styles from "./SearchBar.module.css";

const DEBOUNCE_MS = 250;
const MAX_SUGGESTIONS = 30;
const SERVER_LIMIT = 50;

// Пошукові ендпоінти, які можуть існувати на бекенді
const SEARCH_ENDPOINTS = [
  "/api/products/search",
  "/api/search/products",
  "/api/products/find",
];

// Ключі параметрів пошуку, якщо бекенд фільтрує на /api/products
const QUERY_PARAM_KEYS = ["q", "search", "query", "term", "name"];

const SearchBar = ({
  mobile = false, // режим мобільного пошуку (під хедером)
  onClose, // callback для закриття мобільного пошуку
  autoFocusMobile = false,
  placeholder = "Пошук товарів для ваших улюбленців...",
}) => {
  const { revision: promotionRevision } = usePromotions();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  // Нові стани
  const [isLoading, setIsLoading] = useState(false);
  const [lastCompletedQuery, setLastCompletedQuery] = useState("");
  const fetchSeq = useRef(0); // лічильник запитів для усунення гонок

  const navigate = useNavigate();
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);

  // Axios інстанс (працює і з REACT_APP_API_URL, і з proxy + відносними шляхами)
  const api = useMemo(() => {
    const base = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    return axios.create({ baseURL: base || undefined });
  }, []);

  // Автофокус у мобільному
  useEffect(() => {
    if (autoFocusMobile && inputRef.current) inputRef.current.focus();
  }, [autoFocusMobile]);

  // ==== ХЕЛПЕРИ ====

  // Уніфікатор зображення для item.image || item.images
  const getProductImage = (item) => {
    return pickProductImage(item, "thumbnail");
  };

  // Нормалізація тексту (у т.ч. апострофи)
  const norm = (s) =>
    (s || "")
      .replace(/[’'`ʼ]/g, "'")
      .toString()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();

  // Поля, по яких будуємо "копицю сіна"
  const buildHaystack = (p) =>
    [
      p.name,
      p.nomenclatureName,
      p.productCode,
      p.europeanArticleNumber,
      p.manufacturer,
      p.brand,
      p.category,
      p.tags?.join(" "),
    ]
      .filter(Boolean)
      .join(" ");

  const tokenMatch = (hay, token) => {
    if (!token) return true;
    if (hay.includes(token)) return true;
    const words = hay.split(/[^a-zа-яёіїєґ0-9]+/i).filter(Boolean);
    return words.some((w) => w.startsWith(token));
  };

  const score = (hay, tokens) => {
    const words = hay.split(/[^a-zа-яёіїєґ0-9]+/i).filter(Boolean);
    let s = 0;
    tokens.forEach((t) => {
      if (hay.includes(t)) s += 1;
      if (words.some((w) => w.startsWith(t))) s += 2;
      if (words[0]?.startsWith(t)) s += 1;
    });
    return s;
  };

  // Витягуємо масив товарів із різних форматів відповіді
  const toArray = useCallback((raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;

    const direct =
      raw.items ||
      raw.products ||
      raw.docs ||
      raw.data ||
      raw.rows ||
      raw.result ||
      raw.payload;
    if (Array.isArray(direct)) return direct;

    const nestedCandidates = [
      raw.products?.docs,
      raw.data?.items,
      raw.data?.products,
      raw.data?.docs,
      raw.payload?.items,
      raw.payload?.docs,
      raw.result?.items,
      raw.result?.docs,
    ];
    for (const c of nestedCandidates) if (Array.isArray(c)) return c;

    if (typeof raw === "object") {
      for (const k of Object.keys(raw)) {
        if (Array.isArray(raw[k])) return raw[k];
        if (raw[k] && typeof raw[k] === "object") {
          const deep = toArray(raw[k]);
          if (deep.length) return deep;
        }
      }
    }
    return [];
  }, []);

  // Спроба 1: пошукові ендпоінти
  const trySearchEndpoints = useCallback(
    async (q, signal) => {
      for (const url of SEARCH_ENDPOINTS) {
        try {
          const { data, status } = await api.get(url, {
            params: { q, limit: SERVER_LIMIT },
            signal,
            validateStatus: (s) => s < 500,
          });
          if (status >= 400) continue;
          const arr = toArray(data);
          return arr;
        } catch (error) {
          if (signal.aborted) throw error;
          // наступний
        }
      }
      return null;
    },
    [api, toArray],
  );

  // Спроба 2: /api/products з різними ключами параметрів
  const tryProductsWithQueryParam = useCallback(
    async (q, signal) => {
      for (const key of QUERY_PARAM_KEYS) {
        try {
          const { data, status } = await api.get("/api/products", {
            params: { [key]: q, limit: SERVER_LIMIT },
            signal,
            validateStatus: (s) => s < 500,
          });
          if (status >= 400) continue;
          const arr = toArray(data);
          return arr;
        } catch (error) {
          if (signal.aborted) throw error;
          // наступний ключ
        }
      }
      return null;
    },
    [api, toArray],
  );

  // === Пошук з дебаунсом, лоадером та захистом від гонок ===
  useEffect(() => {
    const controller = new AbortController();
    const mySeq = ++fetchSeq.current;

    const run = async () => {
      const q = query.trim();
      if (!q) {
        if (mySeq === fetchSeq.current && !controller.signal.aborted) {
          setResults([]);
          setActiveIndex(-1);
          setIsLoading(false);
          setLastCompletedQuery("");
          setShowDropdown(false);
        }
        return;
      }

      setIsLoading(true);
      setShowDropdown(true);

      const tokens = norm(q).split(/\s+/).filter(Boolean);

      try {
        let items = [];

        // 1) Пошукові ендпоінти
        const viaSearchEndpoints = await trySearchEndpoints(
          q,
          controller.signal,
        );
        if (viaSearchEndpoints !== null) {
          items = viaSearchEndpoints;
        } else {
          // 2) /api/products з параметрами
          const viaProductsParam = await tryProductsWithQueryParam(
            q,
            controller.signal,
          );
          if (viaProductsParam !== null) {
            items = viaProductsParam;
          } else {
            // 3) Fallback — каталог, який фільтруємо нижче за поточним запитом
            const { data } = await api.get("/api/products", {
              signal: controller.signal,
            });
            items = toArray(data);
          }
        }

        if (mySeq === fetchSeq.current && !controller.signal.aborted) {
          const matchingItems = items
            .filter(isProductSearchSuggestionEligible)
            .map((p) => ({ p, hay: norm(buildHaystack(p)) }))
            .filter(({ hay }) => tokens.every((token) => tokenMatch(hay, token)))
            .sort((a, b) => score(b.hay, tokens) - score(a.hay, tokens))
            .map(({ p }) => p);
          const sortedItems = sortProductsByAvailability(
            matchingItems,
          ).slice(0, MAX_SUGGESTIONS);
          setResults(sortedItems);
          setActiveIndex(sortedItems.length ? 0 : -1);
          setLastCompletedQuery(q);
          setIsLoading(false);
        }
      } catch (err) {
        if (axios.isCancel?.(err)) return;
        // eslint-disable-next-line no-console
        console.error("Помилка пошуку:", err);
        if (mySeq === fetchSeq.current && !controller.signal.aborted) {
          setResults([]);
          setActiveIndex(-1);
          setLastCompletedQuery(q);
          setIsLoading(false);
        }
      }
    };

    const t = setTimeout(run, DEBOUNCE_MS);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [query, api, toArray, trySearchEndpoints, tryProductsWithQueryParam, promotionRevision]);

  // Закриття при кліку поза списком
  useEffect(() => {
    if (!showDropdown) return;
    const handler = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [showDropdown]);

  // UX-хелпери
  const closeIfMobile = () => {
    if (mobile && typeof onClose === "function") onClose();
  };

  const handleSelect = (slugOrItem) => {
    const path = buildProductDetailPath(slugOrItem);
    if (!path) return;
    navigate(path);
    setShowDropdown(false);
    setQuery("");
    setActiveIndex(-1);
    closeIfMobile();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) return;
    if (isLoading || lastCompletedQuery !== q || !results.length) {
      setShowDropdown(true);
      return;
    }
    if (activeIndex >= 0 && results[activeIndex]) {
      handleSelect(results[activeIndex]);
      return;
    }
    navigate(`/products?q=${encodeURIComponent(q)}`);
    setShowDropdown(false);
    closeIfMobile();
  };

  const onKeyDown = (e) => {
    if (!showDropdown || !results.length) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % results.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => (i - 1 + results.length) % results.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (activeIndex >= 0) handleSelect(results[activeIndex]);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setShowDropdown(false);
      setActiveIndex(-1);
    }
  };

  // Підсвітка збігів
  const highlight = (text, q) => {
    const raw = text || "";
    const qTokens = norm(q).split(/\s+/).filter(Boolean);
    if (!qTokens.length) return raw;
    const escaped = qTokens.map((t) =>
      t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
    );
    const rx = new RegExp(`(${escaped.join("|")})`, "ig");
    return raw.split(rx).map((part, idx) => {
      const n = norm(part);
      return escaped.some((e) => new RegExp(`^${e}$`, "i").test(n)) ? (
        <mark key={idx} className={styles.mark}>
          {part}
        </mark>
      ) : (
        <span key={idx}>{part}</span>
      );
    });
  };

  const formatProductValue = (value) => {
    const normalized = value == null ? "" : String(value).trim();
    return normalized || "Н/Д";
  };

  const getPackageSize = (item) => {
    const packageSize = item?.weight == null ? "" : String(item.weight).trim();
    return packageSize;
  };

  const escapeRegExp = (value) =>
    String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const trimPackageSizeFromTitle = (name, packageSize) => {
    const title = name == null ? "" : String(name).trim();
    if (!title || !packageSize) return title;

    const packagePattern = escapeRegExp(packageSize).replace(/\s+/g, "\\s+");
    return title
      .replace(new RegExp(`(?:,\\s*)?\\s*${packagePattern}\\s*$`, "i"), "")
      .trim();
  };

  const getProductTitle = (item) =>
    item?.kind === "Ветеринарні засоби та препарати"
      ? getProductDisplayName(item)
      : trimPackageSizeFromTitle(item?.name, getPackageSize(item));

  const getProductDetails = (item) => {
    const manufacturer = formatProductValue(item?.manufacturer || item?.brand);
    const country =
      item?.countryOfOrigin == null ? "" : String(item.countryOfOrigin).trim();
    const packageSize = getPackageSize(item);

    return [
      `Код: ${formatProductValue(item?.productCode)}`,
      packageSize ? `Розмір упаковки: ${packageSize}` : null,
      `Виробник: ${manufacturer}${country ? `, ${country}` : ""}`,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const isProductOutOfStock = (item) => getProductAvailableQuantity(item) <= 0;

  const hasDiscountPrice = (item) => {
    const price = Number(item?.price);
    const discountPrice = Number(item?.discountPrice);
    return price > 0 && discountPrice > 0 && discountPrice < price;
  };

  const renderProductPrice = (item) => {
    if (isProductOutOfStock(item)) {
      return (
        <span className={styles.outOfStock}>
          <span>Немає в</span>
          <span>наявності</span>
        </span>
      );
    }

    if (hasDiscountPrice(item)) {
      return (
        <span className={styles.dropdownPrice}>
          <span className={styles.oldPrice}>{item.price} ₴</span>
          <span className={styles.discountPrice}>{item.discountPrice} ₴</span>
        </span>
      );
    }

    return item?.price ? (
      <span className={styles.dropdownPrice}>
        <span className={styles.regularPrice}>{item.price} ₴</span>
      </span>
    ) : null;
  };

  // Класи
  const containerClass = mobile
    ? styles.mobileSearchBarContainer
    : styles.searchBarContainer;
  const formClass = mobile ? styles.mobileSearchForm : styles.searchForm;
  const inputClass = mobile ? styles.mobileSearchInput : styles.searchInput;
  const btnClass = mobile ? styles.mobileSearchBtn : styles.searchIconButton;
  const hasQuery = query.trim().length > 0;

  // Для ARIA
  const listboxId = useMemo(
    () => `search-listbox-${Math.random().toString(36).slice(2)}`,
    [],
  );

  return (
    <div className={containerClass} ref={wrapperRef}>
      <form onSubmit={handleSubmit} className={formClass} role="search">
        <input
          ref={inputRef}
          type="text"
          className={inputClass}
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            const nextQuery = e.target.value;
            setQuery(nextQuery);
            setResults([]);
            setActiveIndex(-1);
            setLastCompletedQuery("");
            setIsLoading(Boolean(nextQuery.trim()));
            setShowDropdown(Boolean(nextQuery.trim()));
          }}
          onKeyDown={onKeyDown}
          autoFocus={autoFocusMobile}
          /* ✅ ARIA combobox */
          role="combobox"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          aria-expanded={showDropdown}
          aria-controls={listboxId}
          aria-activedescendant={
            activeIndex >= 0 && results[activeIndex]?._id
              ? `opt-${results[activeIndex]._id}`
              : undefined
          }
        />
        <button type="submit" className={btnClass} aria-label="Знайти">
          <GoSearch />
        </button>
      </form>

      {showDropdown && hasQuery && (
        <div className={styles.dropdownWrap}>
          {isLoading ? (
            <div className={`${styles.searchDropdown} ${styles.emptyPanel}`}>
              <div className={styles.noResults}>Шукаємо…</div>
            </div>
          ) : results.length > 0 ? (
            <ul className={styles.searchDropdown} role="listbox" id={listboxId}>
              {results.map((item, idx) => {
                const productOutOfStock = isProductOutOfStock(item);

                return (
                  <li
                    id={`opt-${item._id}`}
                    role="option"
                    aria-selected={idx === activeIndex}
                    key={item._id}
                    className={`${styles.dropdownItem} ${
                      idx === activeIndex ? styles.active : ""
                    }`}
                    onMouseDown={(e) => e.preventDefault()} // не закривати до click
                    onClick={() => handleSelect(item)}
                    onMouseEnter={() => setActiveIndex(idx)}
                  >
                    <img
                      src={getProductImage(item)}
                      alt={item.name}
                      className={`${styles.dropdownImage} ${
                        productOutOfStock ? styles.outOfStockImage : ""
                      }`}
                      loading="lazy"
                      decoding="async"
                      width={50}
                      height={50}
                    />
                    <div className={styles.dropdownContent}>
                      <div className={styles.dropdownInfo}>
                        <p className={styles.dropdownTitle}>
                          {highlight(getProductTitle(item), query)}
                        </p>
                        <p className={styles.dropdownMetaLine}>
                          {highlight(getProductDetails(item), query)}
                        </p>
                      </div>
                      <div className={styles.dropdownPriceBox}>
                        {renderProductPrice(item)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            // Показуємо повідомлення лише після завершення поточного пошуку
            lastCompletedQuery === query.trim() && (
              <div className={`${styles.searchDropdown} ${styles.emptyPanel}`}>
                <div className={styles.noResults} role="status">Товари не знайдено</div>
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
